import React, { useState } from "react";
import { Mail } from "lucide-react";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
	CraftInputText,
} from "@craft-design-system/core-components-react";
import { apiClient } from "../../services/api";

// Module-level cache — persists for the lifetime of the page session.
// Counts are fetched once and reused on every subsequent modal open.
let _cachedTableCounts = null;
let _isFetchingCounts = false;
let _fetchListeners = [];

async function fetchTableCountsOnce() {
	// Return cache immediately if already loaded
	if (_cachedTableCounts) return _cachedTableCounts;

	// If a fetch is already in flight, wait for it to finish
	if (_isFetchingCounts) {
		return new Promise((resolve) => _fetchListeners.push(resolve));
	}

	_isFetchingCounts = true;
	try {
		const response = await apiClient.get("/database-table-counts");
		_cachedTableCounts = response.data.success
			? response.data.table_counts
			: {};
	} catch {
		_cachedTableCounts = {};
	} finally {
		_isFetchingCounts = false;
		// Resolve any callers that were waiting
		_fetchListeners.forEach((resolve) => resolve(_cachedTableCounts));
		_fetchListeners = [];
	}
	return _cachedTableCounts;
}

const DatabaseExportModal = ({ isOpen, onClose, userEmail = "" }) => {
	const [selectedTable, setSelectedTable] = useState("lief_snr_rv");
	const [selectedFormat, setSelectedFormat] = useState("csv");
	const [isExporting, setIsExporting] = useState(false);
	const [exportStarted, setExportStarted] = useState(false);
	const [tableSearchTerm, setTableSearchTerm] = useState("");
	const [tableCounts, setTableCounts] = useState({});
	const [isLoadingCounts, setIsLoadingCounts] = useState(false);
	const [recipientEmail, setRecipientEmail] = useState(userEmail);
	// Define table descriptions before they're used
	// 17 tables in man_dataplatform_prod_iwh_datahub (latest partition only)
	// 5 views  in man_vehicledatalake_prod_gluedatabase_iwh_datahub (full table)
	const tableDescriptions = {
		// IWH tables
		bedarf_gesamt: "Total requirements data (latest partition)",
		bestand_hist: "Historical inventory data (latest partition)",
		dypp_bestand: "DYPP inventory/stock data (latest partition)",
		emcmla: "EMCOS MLA logistics data (latest partition)",
		kast_bedarfe: "KAST requirements data (latest partition)",
		liab_abruf: "Liability call-off data (latest partition)",
		liab_abruf_sap: "Liability call-off SAP data (latest partition)",
		liab_vertrag: "Liability contract data (latest partition)",
		liab_vertrag_sap: "Liability contract SAP data (latest partition)",
		liefstamm: "Supplier master data (latest partition)",
		lief_snr_rv: "Supplier part number revision data (latest partition)",
		ppkomp: "PP component data (latest partition)",
		ppmdpb: "PP MDP-B planning data (latest partition)",
		v_ppap: "PPAP view data (latest partition)",
		v_sf_allgemein_info:
			"Special release general information view (latest partition)",
		v_sf_matnr_info:
			"Special release material number information view (latest partition)",
		werksteuer_d: "Plant control data (latest partition)",
		// VDL views (man_vehicledatalake_prod_gluedatabase_iwh_datahub — full table, no partition)
		vw_emcfgp: "EMCOS configuration program view (vehicle data lake)",
		vw_emclmd: "EMCOS LMD view (vehicle data lake)",
		vw_emcmls: "EMCOS MLS view (vehicle data lake)",
		vw_emcprj: "EMCOS project view (vehicle data lake)",
		vw_emcstm: "EMCOS STM view (vehicle data lake)",
		// parts_management_raw_db tables (full table, no partition)
		emcos_ods_v_fact_aenderungsnr_sachnr_mls:
			"EMCOS ODS — change number / part number MLS fact view (raw DB)",
		dispo_bestand: "Disposition inventory/stock data (raw DB)",
		// TMS joined view
		emcos_tms:
			"EMCOS TMS — STM × MLS (PBE999/PMM999) × TEIEAK: ident, milestone, status, MET, FET (VDL join)",
		sachstamm_tms:
			"Sachstamm TMS — vw_sachstamm where fremd_snr is not null and sachst_nr starts with PM (VDL)",
	};
	// Reset state when modal opens/closes
	React.useEffect(() => {
		if (!isOpen) {
			setExportStarted(false);
			setTableSearchTerm("");
		} else {
			setRecipientEmail(userEmail);
			// Only fetch if not already cached
			if (_cachedTableCounts) {
				setTableCounts(_cachedTableCounts);
			} else {
				fetchTableCounts();
			}
		}
	}, [isOpen, userEmail]);

	// Fetch table record counts (only called once; subsequent opens use the cache)
	const fetchTableCounts = async () => {
		try {
			setIsLoadingCounts(true);
			const counts = await fetchTableCountsOnce();
			setTableCounts(counts);
		} catch (error) {
			console.error("Error fetching table counts:", error);
			setTableCounts({});
		} finally {
			setIsLoadingCounts(false);
		}
	};
	// Filter tables based on search term
	const getFilteredTables = () => {
		if (!tableSearchTerm) return Object.keys(tableDescriptions);

		return Object.keys(tableDescriptions).filter(
			(tableName) =>
				tableName.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
				tableDescriptions[tableName]
					.toLowerCase()
					.includes(tableSearchTerm.toLowerCase()),
		);
	};

	// Format record count for display
	const formatRecordCount = (tableName) => {
		const count = tableCounts[tableName];
		if (isLoadingCounts) return "...";
		if (count === undefined) return "";
		if (count === -1) return " (error)";
		if (count === 0) return " (empty)";
		return ` (${count.toLocaleString()} records)`;
	};

	// Ensure selected table is valid when search filters change
	React.useEffect(() => {
		const filteredTables = getFilteredTables();
		if (filteredTables.length > 0 && !filteredTables.includes(selectedTable)) {
			setSelectedTable(filteredTables[0]);
		}
	}, [tableSearchTerm, selectedTable]);

	const handleExport = async () => {
		if (!recipientEmail.trim()) {
			alert("Please enter an email address to receive the export.");
			return;
		}

		try {
			setIsExporting(true);

			await apiClient.post("/database-export", {
				table_name: selectedTable,
				export_format: selectedFormat,
				requester_email: recipientEmail.trim(),
			});

			setExportStarted(true);
		} catch (error) {
			console.error("Database export error:", error);
			const status = error?.response?.status;
			const isNetworkOrGatewayError =
				!status || status === 502 || status === 503 || status === 504;
			if (isNetworkOrGatewayError) {
				setExportStarted(true);
			} else {
				const errorMessage =
					error.response?.data?.detail ||
					error.message ||
					"Export failed to start";
				alert(`Export failed: ${errorMessage}`);
			}
		} finally {
			setIsExporting(false);
		}
	};
	const handleManualDownload = async () => {
		// retained for potential future use
	};

	const styles = {
		formGroup: {
			display: "flex",
			flexDirection: "column",
			gap: "6px",
			marginBottom: "16px",
		},
		label: {
			fontSize: "13px",
			fontWeight: "bold",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#333",
			marginBottom: "4px",
			display: "block",
		},
		select: {
			padding: "9px 12px",
			border: "1px solid #ddd",
			borderRadius: "4px",
			fontSize: "14px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			backgroundColor: "white",
			cursor: "pointer",
			width: "100%",
		},
		hint: {
			fontSize: "12px",
			color: "#888",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			marginTop: "4px",
		},
		tableInfo: {
			backgroundColor: "#f8f9fa",
			border: "1px solid #e9ecef",
			borderRadius: "6px",
			padding: "10px 12px",
			fontSize: "13px",
			color: "#555",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			marginTop: "6px",
		},
		infoBox: {
			backgroundColor: "#f0f8ff",
			border: "1px solid #b3d9ff",
			borderRadius: "6px",
			padding: "12px 14px",
			fontSize: "13px",
			color: "#1a5276",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			lineHeight: "1.5",
			marginTop: "16px",
		},
		spinner: {
			width: "14px",
			height: "14px",
			border: "2px solid rgba(255,255,255,0.4)",
			borderTop: "2px solid white",
			borderRadius: "50%",
			animation: "spin 0.8s linear infinite",
			display: "inline-block",
		},
		successIcon: { fontSize: "52px", textAlign: "center", marginBottom: "8px" },
		successTitle: {
			fontSize: "18px",
			fontWeight: "bold",
			color: "#28a745",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			textAlign: "center",
			marginBottom: "16px",
		},
		detailBox: {
			backgroundColor: "#f0f8ff",
			border: "1px solid #b3d9ff",
			borderRadius: "8px",
			padding: "16px 18px",
			fontSize: "14px",
			lineHeight: "1.7",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			marginBottom: "16px",
		},
		confirmBox: {
			backgroundColor: "#e8f5e9",
			border: "1px solid #a5d6a7",
			borderRadius: "8px",
			padding: "14px 16px",
			fontSize: "13px",
			color: "#2e7d32",
			textAlign: "center",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
	};

	return (
		<>
			<style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>

			<CraftModal show={isOpen} allowClickOut={false} onCraftClose={onClose}>
				<CraftModalHeader
					header="Database Export"
					content={
						!exportStarted
							? "Select a table, format and recipient email. The export runs in the background — you will receive a download link by email when ready."
							: "Your export has been queued successfully."
					}
				/>

				<CraftModalBody>
					{!exportStarted ? (
						<>
							{/* Table searchs */}
							<div style={styles.formGroup}>
								<label style={styles.label}>Search Tables:</label>
								<CraftInputText
									type="text"
									placeholder="Type to filter tables..."
									value={tableSearchTerm}
									onInput={(e) => setTableSearchTerm(e.target.value)}
									disabled={isExporting}
									style={{ width: "100%" }}
								/>
							</div>

							{/* Table selector */}
							<div style={styles.formGroup}>
								<label style={styles.label}>Select Table:</label>
								<select
									value={selectedTable}
									onChange={(e) => setSelectedTable(e.target.value)}
									style={styles.select}
									disabled={isExporting}
								>
									<optgroup label="📦 Requirements &amp; Inventory">
										<option value="bedarf_gesamt">
											bedarf_gesamt{formatRecordCount("bedarf_gesamt")}
										</option>
										<option value="bestand_hist">
											bestand_hist{formatRecordCount("bestand_hist")}
										</option>
										<option value="dypp_bestand">
											dypp_bestand{formatRecordCount("dypp_bestand")}
										</option>
										<option value="kast_bedarfe">
											kast_bedarfe{formatRecordCount("kast_bedarfe")}
										</option>
									</optgroup>
									<optgroup label="📋 Liability &amp; Contracts">
										<option value="liab_abruf">
											liab_abruf{formatRecordCount("liab_abruf")}
										</option>
										<option value="liab_abruf_sap">
											liab_abruf_sap{formatRecordCount("liab_abruf_sap")}
										</option>
										<option value="liab_vertrag">
											liab_vertrag{formatRecordCount("liab_vertrag")}
										</option>
										<option value="liab_vertrag_sap">
											liab_vertrag_sap{formatRecordCount("liab_vertrag_sap")}
										</option>
									</optgroup>
									<optgroup label="🛒 Supplier &amp; Procurement">
										<option value="liefstamm">
											liefstamm{formatRecordCount("liefstamm")}
										</option>
										<option value="lief_snr_rv">
											lief_snr_rv{formatRecordCount("lief_snr_rv")}
										</option>
									</optgroup>
									<optgroup label="📊 Planning &amp; PP">
										<option value="ppkomp">
											ppkomp{formatRecordCount("ppkomp")}
										</option>
										<option value="ppmdpb">
											ppmdpb{formatRecordCount("ppmdpb")}
										</option>
									</optgroup>
									<optgroup label="🏗️ EMCOS">
										<option value="emcmla">
											emcmla{formatRecordCount("emcmla")}
										</option>
									</optgroup>
									<optgroup label="🔧 Special Releases &amp; Views">
										<option value="v_ppap">
											v_ppap{formatRecordCount("v_ppap")}
										</option>
										<option value="v_sf_allgemein_info">
											v_sf_allgemein_info
											{formatRecordCount("v_sf_allgemein_info")}
										</option>
										<option value="v_sf_matnr_info">
											v_sf_matnr_info{formatRecordCount("v_sf_matnr_info")}
										</option>
										<option value="werksteuer_d">
											werksteuer_d{formatRecordCount("werksteuer_d")}
										</option>
									</optgroup>
									<optgroup label="🚗 Vehicle Data Lake (EMCOS Views)">
										<option value="vw_emcfgp">
											vw_emcfgp{formatRecordCount("vw_emcfgp")}
										</option>
										<option value="vw_emclmd">
											vw_emclmd{formatRecordCount("vw_emclmd")}
										</option>
										<option value="vw_emcmls">
											vw_emcmls{formatRecordCount("vw_emcmls")}
										</option>
										<option value="vw_emcprj">
											vw_emcprj{formatRecordCount("vw_emcprj")}
										</option>
										<option value="vw_emcstm">
											vw_emcstm{formatRecordCount("vw_emcstm")}
										</option>
									</optgroup>
									<optgroup label="🗄️ TMS">
										<option value="emcos_ods_v_fact_aenderungsnr_sachnr_mls">
											emcos_ods_v_fact_aenderungsnr_sachnr_mls
											{formatRecordCount(
												"emcos_ods_v_fact_aenderungsnr_sachnr_mls",
											)}
										</option>
										<option value="dispo_bestand">
											dispo_bestand{formatRecordCount("dispo_bestand")}
										</option>
										<option value="emcos_tms">
											emcos_tms{formatRecordCount("emcos_tms")}
										</option>
										<option value="sachstamm_tms">
											sachstamm_tms{formatRecordCount("sachstamm_tms")}
										</option>
									</optgroup>
								</select>

								<div style={styles.tableInfo}>
									{tableDescriptions[selectedTable]}
									{isLoadingCounts && (
										<span
											style={{
												marginLeft: "8px",
												fontStyle: "italic",
												color: "#888",
											}}
										>
											Loading record counts...
										</span>
									)}
								</div>
							</div>

							{/* Format selector */}
							<div style={styles.formGroup}>
								<label style={styles.label}>Export Format:</label>
								<select
									value={selectedFormat}
									onChange={(e) => setSelectedFormat(e.target.value)}
									style={styles.select}
									disabled={isExporting}
								>
									<option value="csv">CSV (.csv)</option>
								</select>
							</div>

							{/* Email input */}
							<CraftInputText
								type="email"
								label="Send export to email *"
								required
								value={recipientEmail}
								onInput={(e) => setRecipientEmail(e.target.value)}
								placeholder="your@email.com"
								disabled={isExporting}
								style={{ width: "100%", marginBottom: "4px" }}
							/>
							<span style={styles.hint}>
								A download link will be emailed to this address once the export
								is ready.
							</span>

							{/* Background export info */}
							<div style={styles.infoBox}>
								<strong>✨ Background Export:</strong> The export runs in the
								background — you can close this window immediately after
								clicking Export. The file will be emailed to you when ready.
								<br />
								Depending on the dataset size this may take 5–15 minutes.
							</div>

							{/* Large dataset warning */}
							{tableCounts[selectedTable] &&
								tableCounts[selectedTable] > 500000 && (
									<div
										style={{
											...styles.infoBox,
											backgroundColor: "#fff8e1",
											borderColor: "#ffe082",
											color: "#7b5800",
											marginTop: "12px",
										}}
									>
										<strong>📊 Large Dataset:</strong> This table contains{" "}
										<strong>
											{tableCounts[selectedTable].toLocaleString()} records
										</strong>
										. The export runs in the background — you'll receive an
										email with the download link.
									</div>
								)}
						</>
					) : (
						/* Confirmation screen */
						<>
							<div style={styles.successIcon}>📬</div>
							<div style={styles.successTitle}>Export Started!</div>
							<div style={styles.detailBox}>
								<div>
									<strong>Table:</strong> {selectedTable}
								</div>
								<div>
									<strong>Format:</strong> {selectedFormat.toUpperCase()}
								</div>
								<div>
									<strong>Recipient:</strong> {recipientEmail}
								</div>
							</div>
							<div style={styles.confirmBox}>
								✅ The export is processing in the background.
								<br />
								<strong>You can close this window now.</strong>
								<br />A download link will be emailed to{" "}
								<strong>{recipientEmail}</strong> when ready.
							</div>
						</>
					)}
				</CraftModalBody>

				<CraftModalFooter>
					{!exportStarted ? (
						<>
							<CraftButton
								variant="plain"
								size="m"
								onClick={onClose}
								disabled={isExporting}
							>
								Cancel
							</CraftButton>
							<CraftButton
								variant="primary"
								size="m"
								onClick={handleExport}
								disabled={isExporting || !recipientEmail.trim()}
							>
								{isExporting ? (
									<span
										style={{
											display: "flex",
											alignItems: "center",
											gap: "7px",
										}}
									>
										<span style={styles.spinner} />
										Starting…
									</span>
								) : (
									<span
										style={{
											display: "flex",
											alignItems: "center",
											gap: "7px",
										}}
									>
										<Mail size={15} />
										Export &amp; Email {selectedFormat.toUpperCase()}
									</span>
								)}
							</CraftButton>
						</>
					) : (
						<>
							<CraftButton
								variant="plain"
								size="m"
								onClick={() => {
									setExportStarted(false);
									setTableSearchTerm("");
								}}
							>
								New Export
							</CraftButton>
							<CraftButton variant="primary" size="m" onClick={onClose}>
								Close
							</CraftButton>
						</>
					)}
				</CraftModalFooter>
			</CraftModal>
		</>
	);
};

export default DatabaseExportModal;
